import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Spin } from 'antd';
import {
  DollarCircleOutlined,
  CarOutlined,
  FieldTimeOutlined,
  FundOutlined,
} from '@ant-design/icons';
import ApiClient from '../../../methods/api/apiClient';

const COLORS = {
  primary: '#3E549D',
  success: '#27ae60',
  warning: '#f39c12',
  danger: '#e74c3c',
};

const FinancialMetrics = ({ board_id, dateRange, summaryData }) => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    revenuePerMile: null,
    avgLoadValue: null,
    avgDistance: null,
    totalLoadValue: null,
  });

  useEffect(() => {
    // Use pre-fetched summary data if available (from consolidated endpoint)
    if (summaryData && summaryData.financials) {
      const f = summaryData.financials;
      setMetrics({
        revenuePerMile: f.revenuePerMile || 0,
        avgLoadValue: f.avgValueOfLoad || 0,
        avgDistance: f.avgDistancePerLoad || 0,
        totalLoadValue: f.sumLoadValue || 0,
      });
      setLoading(false);
      return;
    }

    // Fallback: fetch individually if summaryData not provided
    const buildParams = () => {
      const params = {};
      if (board_id) params.board_id = board_id;
      if (dateRange?.startDate) params.startDate = dateRange.startDate;
      if (dateRange?.endDate) params.endDate = dateRange.endDate;
      return params;
    };

    const fetchMetrics = () => {
      setLoading(true);
      const params = buildParams();

      const calls = [
        ApiClient.get('statistics/revenue-per-mile', params, '', true)
          .then(res => (res?.success || res?.code === 200) ? res?.data : null)
          .catch(() => null),
        ApiClient.get('statistics/average/value-load', params, '', true)
          .then(res => (res?.success || res?.code === 200) ? res?.data : null)
          .catch(() => null),
        ApiClient.get('statistics/average/distance-per-load', params, '', true)
          .then(res => (res?.success || res?.code === 200) ? res?.data : null)
          .catch(() => null),
        ApiClient.get('statistics/average/sum-load-value', params, '', true)
          .then(res => (res?.success || res?.code === 200) ? res?.data : null)
          .catch(() => null),
      ];

      Promise.all(calls)
        .then(([rpm, avgVal, avgDist, totalVal]) => {
          setMetrics({
            revenuePerMile: rpm,
            avgLoadValue: avgVal,
            avgDistance: avgDist,
            totalLoadValue: totalVal,
          });
        })
        .finally(() => setLoading(false));
    };

    fetchMetrics();
  }, [board_id, dateRange, summaryData]);

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return 'N/A';
    const num = typeof val === 'object' ? (val.average || val.value || val.total || 0) : val;
    return `$${Number(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDistance = (val) => {
    if (val === null || val === undefined) return 'N/A';
    const num = typeof val === 'object' ? (val.average || val.value || val.total || 0) : val;
    return `${Math.round(Number(num)).toLocaleString()} mi`;
  };

  const extractNumber = (val) => {
    if (val === null || val === undefined) return 0;
    if (typeof val === 'object') return val.average || val.value || val.total || 0;
    return Number(val) || 0;
  };

  const cards = [
    {
      title: 'Revenue / Mile',
      value: formatCurrency(metrics.revenuePerMile),
      icon: <DollarCircleOutlined style={{ fontSize: 28, color: COLORS.success }} />,
      color: COLORS.success,
      raw: extractNumber(metrics.revenuePerMile),
    },
    {
      title: 'Avg Load Value',
      value: formatCurrency(metrics.avgLoadValue),
      icon: <FundOutlined style={{ fontSize: 28, color: COLORS.primary }} />,
      color: COLORS.primary,
      raw: extractNumber(metrics.avgLoadValue),
    },
    {
      title: 'Avg Distance / Load',
      value: formatDistance(metrics.avgDistance),
      icon: <CarOutlined style={{ fontSize: 28, color: COLORS.warning }} />,
      color: COLORS.warning,
      raw: extractNumber(metrics.avgDistance),
    },
    {
      title: 'Total Load Value',
      value: formatCurrency(metrics.totalLoadValue),
      icon: <FieldTimeOutlined style={{ fontSize: 28, color: COLORS.danger }} />,
      color: COLORS.danger,
      raw: extractNumber(metrics.totalLoadValue),
    },
  ];

  return (
    <Card
      title={
        <span style={{ fontWeight: 600, fontSize: 15 }}>
          <DollarCircleOutlined style={{ color: COLORS.primary, marginRight: 8 }} />
          Financial Metrics
        </span>
      }
      bordered={false}
      style={{ borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
      styles={{ body: { padding: '16px 20px' } }}
    >
      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          {cards.map((card, idx) => (
            <Col xs={12} sm={12} md={6} key={idx}>
              <div style={{
                textAlign: 'center',
                padding: '12px 8px',
                borderRadius: 8,
                background: '#f9fafb',
                border: '1px solid #f0f0f0',
              }}>
                <div style={{ marginBottom: 8 }}>{card.icon}</div>
                <Statistic
                  title={<span style={{ fontSize: 11, color: '#888' }}>{card.title}</span>}
                  value={card.value}
                  valueStyle={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: card.color,
                  }}
                />
              </div>
            </Col>
          ))}
        </Row>
        {!loading && cards.every(c => c.value === 'N/A') && (
          <div style={{ textAlign: 'center', color: '#999', padding: '12px 0', fontSize: 13 }}>
            No financial data available for the selected period.
          </div>
        )}
      </Spin>
    </Card>
  );
};

export default FinancialMetrics;
